import { Plus, Minus } from "lucide-react";
import FitNumber from "../../common/FitNumber";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../../components/common/GeneralComponent";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { fetchData } from "../../../utils/functions";
import { PieChart } from "@mui/x-charts/PieChart";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";
import NoData, { hasData } from "../../common/NoData";
import SharedDonut from "../../common/DonutChart";
import DashboardBarChart from "../../common/DashboardBarChart";
import {
  BRAND,
  NEUTRAL,
  STATUS_COLORS,
  getPaletteColor,
} from "../../../utils/chartTheme";
import { formatCount } from "../../../utils/numberFormat";
// Enhanced Donut Chart Component
const OWNERSHIP_TYPES = ["Government", "Private", "PPHI", "NGO", "Other"];
const DonutChart = ({ data, centerLabel }) => {
  const valueOf = Object.fromEntries((data || []).map((item) => [item.name, item.value]));
  return (
    <SharedDonut
      items={OWNERSHIP_TYPES.map((name, index) => ({
        name,
        value: valueOf[name] || 0,
        color: getPaletteColor(index),
      }))}
      centerLabel={centerLabel}
    />
  );
};
const HealthFacilityDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState("facilities");
  const [districts, setDistricts] = useState([]);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [ownershipData, setOwnershipData] = useState(null);
  const [buildingConditionData, setBuildingConditionData] = useState([]);
  const [waterStorageData, setWaterStorageData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [patientStatsData, setPatientStatsData] = useState([]);
  const [districtStaffData, setDistrictStaffData] = useState([]);
  const [handwashStationsData, setHandwashStationsData] = useState([]);
  const [latrinesData, setLatrinesData] = useState({
    functional_total: 0,
    non_functional_total: 0,
  });
  const [healthStatusData, setHealthStatusData] = useState({
    surveyed: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
  });
  const [healthFacilityTypeData, setHealthFacilityTypeData] = useState([]);
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(true);
  // Format ownership data for donut chart
  const formattedOwnershipData = useMemo(() => {
    if (!ownershipData) return [];

    const colorMap = {
      Government: getPaletteColor(0),
      Private: getPaletteColor(1),
      PPHI: getPaletteColor(2),
      NGO: getPaletteColor(3),
      Other: getPaletteColor(4),
    };

    return Object.entries(ownershipData)
      .map(([key, value]) => ({
        name: key,
        value: Number(value) || 0,
        color: colorMap[key] || getPaletteColor(4),
      }))
      .filter((item) => item.value > 0);
  }, [ownershipData]);
  // Helper function to format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  };
  // Fetch districts
  useEffect(() => {
    fetchDistricts();
  }, []);
  // Fetch all health facility data
  useEffect(() => {
    fetchHealthFacilityData();
  }, [filters]);
  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    }
  };
  const fetchHealthFacilityData = async (appliedFilters = filters) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOwnershipData(appliedFilters),
        fetchBuildingConditionData(appliedFilters),
        fetchWaterStorageData(appliedFilters),
        fetchDistrictStaffData(appliedFilters),
        fetchHandwashStationsData(appliedFilters),
        fetchLatrinesData(appliedFilters),
        fetchHealthStatusData(appliedFilters),
        fetchHealthFacilityTypesData(appliedFilters),
      ]);
    } catch (error) {
      console.error("Error fetching health facility data:", error);
    }
    setLoading(false);
  };

  const fetchHealthFacilityTypesData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "health-facility-types",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setHealthFacilityTypeData(response?.data || []);
    } catch (error) {
      console.error("Error fetching health facility types data:", error);
      setHealthFacilityTypeData([]);
    }
  };
  // Fetch Health Status Data (surveyed, approved, rejected)
  const fetchHealthStatusData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "health-approved-reject-count",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      const { surveyed = 0, approved = 0, rejected = 0 } = response?.data || {};
      const pending = surveyed - approved - rejected;
      setHealthStatusData({
        surveyed,
        approved,
        rejected,
        pending,
      });
    } catch (error) {
      console.error("Error fetching health status data:", error);
      setHealthStatusData({
        surveyed: 0,
        approved: 0,
        rejected: 0,
        pending: 0,
      });
    }
  };
  // Handwash Stations data fetch function
  const fetchHandwashStationsData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "Handwash-Stations-Status",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setHandwashStationsData(response?.data || []);
    } catch (error) {
      console.error("Error fetching handwash stations data:", error);
      setHandwashStationsData([]);
    }
  };
  const HandwashResourcesDonut = ({ functional, nonFunctional }) => (
    <SharedDonut
      items={[
        { name: "Functional", value: functional || 0, color: BRAND.teal },
        { name: "Non-Functional", value: nonFunctional || 0, color: BRAND.gold },
      ]}
    />
  );
  const handwashStats = useMemo(() => {
    if (!handwashStationsData || handwashStationsData.length === 0) {
      return { functional: 0, nonFunctional: 0, total: 0 };
    }
    const functional = handwashStationsData.filter(
      (item) => item.handwash_stations_status === "Functional"
    ).length;
    const nonFunctional = handwashStationsData.filter(
      (item) => item.handwash_stations_status === "Non-Functional"
    ).length;
    return {
      functional,
      nonFunctional,
      total: functional + nonFunctional,
    };
  }, [handwashStationsData]);

  const fetchOwnershipData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "Health-Ownerships",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      const ownershipCounts = {
        Government: 0,
        Private: 0,
        PPHI: 0,
        NGO: 0,
        Other: 0,
      };

      if (response?.data) {
        response.data.forEach((item) => {
          const ownershipType = item.health_ownership;
          if (ownershipType) {
            // Map "Others" to "Other" for consistency
            const normalizedType =
              ownershipType === "Others" ? "Other" : ownershipType;

            // Only count if it's one of our expected categories
            if (ownershipCounts.hasOwnProperty(normalizedType)) {
              ownershipCounts[normalizedType]++;
            } else {
              // Any unexpected category goes to "Other"
              ownershipCounts.Other++;
            }
          }
        });
      }
      setOwnershipData(ownershipCounts);
    } catch (error) {
      console.error("Error fetching health ownership data:", error);
      setOwnershipData({
        Government: 0,
        Private: 0,
        PPHI: 0,
        NGO: 0,
        Other: 0,
      });
    }
  };
  const fetchBuildingConditionData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "Health-Building-Conditions",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setBuildingConditionData(response?.data || []);
    } catch (error) {
      console.error("Error fetching building condition data:", error);
      setBuildingConditionData([]);
    }
  };
  const fetchWaterStorageData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "Health-Water-Storage",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setWaterStorageData(response?.data || []);
    } catch (error) {
      console.error("Error fetching water storage data:", error);
      setWaterStorageData([]);
    }
  };
  const fetchDistrictStaffData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "district-wise-health-staff",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setDistrictStaffData(response?.data || []);
    } catch (error) {
      console.error("Error fetching district staff data:", error);
      setDistrictStaffData([]);
    }
  };
  const fetchLatrinesData = async (appliedFilters = filters) => {
    try {
      const response = await fetchData(
        "Latrines-Total",
        "GET",
        null,
        {},
        appliedFilters,
        navigate,
        "/health-facility-dashboard",
        dispatch
      );
      setLatrinesData(
        response?.data || { functional_total: 0, non_functional_total: 0 }
      );
    } catch (error) {
      console.error("Error fetching latrines data:", error);
      setLatrinesData({ functional_total: 0, non_functional_total: 0 });
    }
  };
  // Building Condition Pie Chart with Compact Legend
  const BuildingConditionPie = ({ buildingConditionData }) => {
    const chartData = useMemo(() => {
      const counts = {};
      buildingConditionData.forEach((item) => {
        counts[item.building_condition] =
          (counts[item.building_condition] || 0) + 1;
      });
      const total = Object.values(counts).reduce(
        (sum, count) => sum + count,
        0
      );
      return Object.entries(counts).map(([condition, count], idx) => ({
        id: idx,
        value: count,
        label: condition,
        count: count,
        percentage: ((count / total) * 100).toFixed(1),
        color: getPaletteColor(idx),
      }));
    }, [buildingConditionData]);
    if (!buildingConditionData || buildingConditionData.length === 0) {
      return <NoData />;
    }
    return (
      <div className="flex flex-col items-center h-full justify-center space-y-4">
        {/* Pie Chart */}
        <div className="mb-4 flex-shrink-0">
          <PieChart
            series={[
              {
                data: chartData.map((item) => ({
                  id: item.id,
                  value: item.value,
                  color: item.color,
                })),
                innerRadius: 30,
                outerRadius: 80,
                paddingAngle: 2,
                cornerRadius: 4,
                highlightScope: { fade: "global", highlight: "item" },
              },
            ]}
            width={250}
            height={200}
            slotProps={{
              legend: {
                hidden: true,
              },
            }}
          />
        </div>
        {/* Compact Mini Legend */}
        <div className="flex flex-wrap justify-center gap-3 w-full px-2">
          {chartData.map((item, index) => (
            <div
              key={item.id}
              className="flex items-center space-x-2 text-xs min-w-0"
            >
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-gray-600 font-medium leading-tight">
                {item.label}
              </span>
              <span className="text-gray-800 font-bold whitespace-nowrap">
                ({item.count})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const WaterStorage = ({ waterStorageData }) => {
    const counts = {};
    (waterStorageData || []).forEach((item) => {
      counts[item.water_storage_type] = (counts[item.water_storage_type] || 0) + 1;
    });
    const items = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    return (
      <div className="w-full">
        <DashboardBarChart horizontal items={items} unit="facilities" />
      </div>
    );
  };
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  // const applyFilters = () => {
  // fetchHealthFacilityData();
  // };
  const applyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    fetchHealthFacilityData();
  };
  const resetFilters = () => {
    setFilters({
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    });
    setTimeout(() => {
      fetchHealthFacilityData();
    }, 100);
  };
  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };
  const has = {
    surveyed: hasData(healthStatusData.surveyed),
    handwash: hasData(handwashStats.functional, handwashStats.nonFunctional),
    ownership: hasData(formattedOwnershipData.map((item) => item.value)),
    types: hasData((healthFacilityTypeData || []).map((item) => item.total)),
    storage: (waterStorageData || []).length > 0,
    latrines: hasData(latrinesData.functional_total, latrinesData.non_functional_total),
    building: (buildingConditionData || []).length > 0,
    staff: (districtStaffData || []).length > 0,
  };
  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;
  const ActivityTypeCard = ({ title, count, variant = "default" }) => {
    const borderColors = {
      default: "#ededed",
      approved: BRAND.teal,
      rejected: getPaletteColor(2),
      pending: STATUS_COLORS.planned,
    };
    const borderColor = borderColors[variant] || borderColors.default;
    return (
      <div>
        <div
          className="bg-[#f7f7f7] text-white h-28 p-2.5 rounded-3xl border-2 hover:border-teal-300 transition-all duration-300 hover:shadow-md"
          style={{ borderColor }}
        >
          <h3 className="text-2xl text-center mt-4 mb-0 text-[#3A3A3A] p-0 font-bold text-balance">
            <FitNumber value={count ?? 0} text={formatCount(count ?? 0)} />
          </h3>
          <h6 className="text-xs text-center p-0 m-0 text-[#787878] font-semibold">
            {title}
          </h6>
        </div>
      </div>
    );
  };
  const OutcomesCard = ({ title, value, icon, currency }) => (
    <div className="text-center">
      <div
        className={`rounded-2xl text-white text-xl w-10 h-10 mx-auto my-1 flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 ${
          currency === "€" ? "bg-yellow-600" : "bg-teal-700"
        }`}
      >
        {icon}
      </div>
      <h5 className="text-xs m-0 text-gray-500 font-bold">{title}</h5>
      <h4 className="text-xl m-0 p-0 text-black font-bold drop-shadow-sm text-balance">
        {value}
      </h4>
    </div>
  );
  return (
    <div className="w-full px-4 bg-gray-50">
      {/* Filters */}
      <div className="w-full flex flex-col justify-between">
        <DashboardSearchFilters
          accordionTitle="Filters"
          defaultExpanded={false}
          fetchOnChange={false} // Important: don't fetch on every change
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          dateFields={["date_of_data_collection"]}
          onApply={(appliedFilters) => applyFilters(appliedFilters)} // triggered only on Apply
        />
      </div>
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
          <p className="mt-2 text-gray-600">Loading health facility data...</p>
        </div>
      )}
      {/* Main Content */}
      <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 rounded-lg shadow-sm">
        <div className="space-y-4">
          {!loading && !Object.values(has).some(Boolean) && (
            <NoData className="py-16" />
          )}
          {/* Top Row - Health Surveyed, Resources, Ownership */}
          {showRow(has.surveyed, has.handwash, has.ownership) && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Health Surveyed */}
            <div className="lg:col-span-4">
              <div className="text-center mb-3">
                <h3 className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
                  Health Surveyed ({formatCount(healthStatusData.surveyed)})
                </h3>
                {panel(has.surveyed,
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <ActivityTypeCard
                    title="Approved"
                    count={healthStatusData.approved}
                    variant="approved"
                  />
                  <ActivityTypeCard
                    title="Pending"
                    count={healthStatusData.pending}
                    variant="pending"
                  />
                  <ActivityTypeCard
                    title="Rejected"
                    count={healthStatusData.rejected}
                    variant="rejected"
                  />
                </div>)}
              </div>
            </div>
            {/* Resources */}
            {/* Resources */}
            <div className="lg:col-span-4">
              <div className="text-center mb-3">
                <h3 className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
                  Handwash Resources
                </h3>
                {panel(has.handwash,
                <div className="mt-3">
                  <HandwashResourcesDonut
                    functional={handwashStats.functional}
                    nonFunctional={handwashStats.nonFunctional}
                  />
                </div>)}
              </div>
            </div>
            {/* Ownership */}
            <div className="lg:col-span-4">
              <div className="text-center mb-3">
                <h3 className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
                  Health Ownership
                </h3>
                {panel(has.ownership,
                <div className="mt-3">
                  <DonutChart
                    data={formattedOwnershipData}
                    centerValue={formattedOwnershipData.reduce(
                      (sum, item) => sum + item.value,
                      0
                    )}
                    centerLabel="Total"
                  />
                </div>)}
              </div>
            </div>
          </div>
          )}
          {/* Second Row - Health Facility Types Bar Chart */}
          {showRow(has.types) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-12">
              <div className="bg-white flex flex-col">
                <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center mb-4 text-balance">
                  Health Facility Types
                </h3>
                <div className="w-full">
                  <DashboardBarChart
                    items={(healthFacilityTypeData || []).map((item) => ({
                      name: item.health_facility_type,
                      count: item.total || 0,
                    }))}
                    unit="facilities"
                    height={400}
                  />
                </div>
              </div>
            </div>
          </div>
          </>)}
          {/* Third Row - Water Storage Types, Latrines Status, Building Conditions */}
          {showRow(has.storage, has.latrines, has.building) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Water Storage Types */}
            <div className="lg:col-span-4">
              <div className="bg-white flex flex-col min-h-80">
                <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance">
                  Water Storage Types
                </h3>
                <div className="flex-1 flex justify-center items-start">
                  <WaterStorage waterStorageData={waterStorageData} />
                </div>
              </div>
            </div>
            {/* Latrines Status */}
            <div className="lg:col-span-4">
              <div className="bg-white flex flex-col h-80">
                <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance">
                  Latrines Status
                </h3>
                <div className="flex-1 flex justify-center items-center">
                  <div className="w-full">
                    <DashboardBarChart
                      items={[
                        { name: "Functional", count: latrinesData.functional_total },
                        { name: "Non-Functional", count: latrinesData.non_functional_total },
                      ]}
                      unit="latrines"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Building Conditions */}
            <div className="lg:col-span-4">
              <div className="bg-white flex flex-col h-80">
                <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance">
                  Building Conditions
                </h3>
                <div className="flex-1 flex justify-center items-center">
                  <BuildingConditionPie
                    buildingConditionData={buildingConditionData}
                  />
                </div>
              </div>
            </div>
          </div>
          </>)}
          {/* Last Row - Full Width Table: District-wise Health Staff */}
          {showRow(has.staff) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-8 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-12">
              <div className="text-center">
                <h3 className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
                  District-wise Health Staff
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-white font-medium bg-[#137e81]">
                          District
                        </th>
                        <th className="px-4 py-3 text-center text-white font-medium bg-[#137e81]">
                          Doctors
                        </th>
                        <th className="px-4 py-3 text-center text-white font-medium bg-[#137e81]">
                          Vaccinators
                        </th>
                        <th className="px-4 py-3 text-center text-white font-medium bg-[#137e81]">
                          Other Staff
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {districtStaffData && districtStaffData.length > 0 ? (
                        districtStaffData.map((item, index) => (
                          <tr
                            key={item.district_id}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#F6F7F9" : "#FFFFFF",
                            }}
                            className="hover:bg-gray-50 transition-colors duration-200"
                          >
                            <td className="px-4 py-3 text-gray-800 text-left border-b border-gray-200 font-medium">
                              {item.district_name}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-800 border-b border-gray-200">
                              {formatCount(parseInt(item.total_doctors) || 0)}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-800 border-b border-gray-200">
                              {formatCount(
                                parseInt(item.total_vaccinators) || 0
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-800 border-b border-gray-200 font-semibold">
                              {formatCount(
                                parseInt(item.total_health_staff) || 0
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-4 py-8 text-center text-gray-500 border-b border-gray-200"
                          >
                            {loading ? "Loading data..." : "No data available"}
                          </td>
                        </tr>
                      )}
                      {/* Total Row */}
                      <tr className="bg-[#137e81] text-white font-bold">
                        <td className="px-4 py-3 font-bold text-left text-white border-b border-gray-200">
                          Total
                        </td>
                        <td className="px-4 py-3 text-center text-white border-b border-gray-200">
                          {formatCount(
                            districtStaffData.reduce(
                              (sum, item) =>
                                sum + (parseInt(item.total_doctors) || 0),
                              0
                            )
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-white border-b border-gray-200">
                          {formatCount(
                            districtStaffData.reduce(
                              (sum, item) =>
                                sum + (parseInt(item.total_vaccinators) || 0),
                              0
                            )
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-white border-b border-gray-200">
                          {formatCount(
                            districtStaffData.reduce(
                              (sum, item) =>
                                sum + (parseInt(item.total_health_staff) || 0),
                              0
                            )
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
};
export default HealthFacilityDashboard;
